/**
 * ThemeProvider - 테마 상태 관리 컴포넌트
 *
 * 다크 모드/라이트 모드 전환을 위한 전역 상태 관리.
 *
 * Features:
 * - 'light', 'dark', 'system' 3가지 테마 옵션
 * - localStorage 영속화
 * - 시스템 테마 감지 및 자동 적용
 * - HTML class 자동 토글
 *
 * @module ThemeProvider
 */

import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { ThemeContext, type Theme, type ResolvedTheme, type ThemeContextValue } from './theme-context';

const THEME_STORAGE_KEY = 'ledger-theme';

/**
 * 시스템 테마 감지
 */
function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * 저장된 테마 로드
 */
function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // localStorage 접근 실패 시 기본값 반환
  }
  return 'system';
}

/**
 * HTML 클래스 업데이트
 */
function updateHtmlClass(resolvedTheme: ResolvedTheme) {
  if (typeof document === 'undefined') return;

  const html = document.documentElement;
  if (resolvedTheme === 'dark') {
    html.classList.add('dark');
  } else {
    html.classList.remove('dark');
  }
}

interface ThemeProviderProps {
  children: ReactNode;
  /**
   * 기본 테마 (옵션)
   */
  defaultTheme?: Theme;
}

/**
 * ThemeProvider Component
 *
 * @example
 * ```tsx
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({ children, defaultTheme }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => defaultTheme ?? getStoredTheme());
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  // 실제 적용될 테마 계산
  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme;
  const isDark = resolvedTheme === 'dark';

  // 시스템 테마 변경 감지
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // HTML 클래스 업데이트
  useEffect(() => {
    updateHtmlClass(resolvedTheme);
  }, [resolvedTheme]);

  // 테마 변경 핸들러
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch {
      // localStorage 저장 실패 무시
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
      isDark,
    }),
    [theme, resolvedTheme, setTheme, isDark]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
